/**
 * Weekplan — de werkweek (ma-vr) in één blik, gebaseerd op het Vandaag-concept.
 *
 * Boven: per dag de agenda (dense, uit je eigen Outlook-agenda) + geplande taken.
 * Onder (±50% van het scherm): de todolijst — Airtable-backlog, Microsoft To-Do
 * én in Outlook gevlagde mails (🚩 komen automatisch in To-Do's flagged-lijst).
 *
 * Slepen: lijst → dag plant de taak (MS To-Do/vlag wordt dan een echte
 * Airtable-taak, mét assignee — blijft dus staan na refresh); dag → dag verzet.
 * Prio = stoplicht (rood/oranje/groen); klik op een taak opent details met een
 * groot beschrijvingsveld en projectkoppeling.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Paper, Typography, Chip, Stack, CircularProgress, IconButton, Tooltip, Checkbox,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Autocomplete,
  ToggleButtonGroup, ToggleButton, Alert,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FlagIcon from '@mui/icons-material/Flag';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  DndContext, PointerSensor, useSensor, useSensors, useDraggable, useDroppable,
  pointerWithin, rectIntersection,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL, AI_ENGINE_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson, meldFout } from '../../utils/apiFout';
import { fetchDayEvents } from '../../utils/graphCalendar';
import { searchMail, getMailBody, fetchArchivedSubjects } from '../../utils/graphMailSearch';
import {
  initMicrosoftTodoService, getTodoLists, getTasksFromList, completeTask,
  createTask, getDefaultList, reopenTask, normaliseerChecklist, setChecklistItem, addChecklistItem, deleteChecklistItem, updateTask, todoDueDate } from '../../utils/microsoftTodoService';
import PageHeader from '../../ui-components/PageHeader';
import TaakPaneel from '../../ui-components/TaakPaneel/TaakPaneel';
import toast from 'react-hot-toast';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';
import { bevestig } from '../../utils/bevestig';

// ── Prio-stoplicht: mapping op Airtable PrioNr ───────────────────────────────
const PRIO_COLORS = [
  { key: 'rood', prioNr: 'Prio 1', color: '#ef4444', label: 'Hoog' },
  { key: 'oranje', prioNr: 'Prio 2', color: '#f59e0b', label: 'Normaal' },
  { key: 'groen', prioNr: 'Prio 4', color: '#10b981', label: 'Laag' },
];
const prioColor = (t) => {
  if (t.source === 'mstodo') return t.importance === 'high' ? '#ef4444' : t.importance === 'low' ? '#10b981' : '#f59e0b';
  if (t.priority === 'Prio 1' || t.priority === 'Deadline') return '#ef4444';
  if (t.priority === 'Prio 2' || t.priority === 'Prio 3') return '#f59e0b';
  return '#10b981';
};

const WEEKDAY_NAMES = ['Zondag', 'Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag'];
const dayNameOf = (dateStr) => WEEKDAY_NAMES[new Date(`${dateStr}T12:00:00`).getDay()];

// n werkdagen verder/terug (weekend telt niet mee)
function addWorkdays(date, n) {
  const d = new Date(date);
  const step = n >= 0 ? 1 : -1;
  let left = Math.abs(n);
  while (left > 0) {
    d.setDate(d.getDate() + step);
    if (d.getDay() !== 0 && d.getDay() !== 6) left -= 1;
  }
  return d;
}

const iso = (d) => d.toISOString().split('T')[0];

// Duur van een agenda-afspraak compact ("30m" / "1u" / "1u30"), leeg bij all-day/onbekend.
const fmtEventDuration = (ev) => {
  if (!ev?.start || !ev?.end || ev.isAllDay) return '';
  const mins = Math.round((new Date(ev.end) - new Date(ev.start)) / 60000);
  if (mins <= 0) return '';
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60), m = mins % 60;
  return m ? `${h}u${String(m).padStart(2, '0')}` : `${h}u`;
};

// ── Draggable taak-kaartje — met subtiel afvink-rondje (verschijnt bij hover) ──
// Presentational chip — krijgt de dnd-bindings van buiten (draggable óf sortable).
function TaskChipInner({ task, onClick, onComplete, onNextWeek, dense, blocks, heightPx,
  setNodeRef, attributes, listeners, transform, transition, isDragging }) {
  // Tijdlijn: exacte hoogte = duur (heightPx). Anders (Blokken-legacy): ~20px/uur, gecapt.
  const isTimeline = heightPx != null;
  const blockHeight = isTimeline ? heightPx : (blocks ? Math.min(150, 26 + (Number(task.hours) || 0.5) * 20) : undefined);
  return (
    <Paper ref={setNodeRef} {...attributes} {...listeners} elevation={0}
      onClick={() => onClick?.(task)}
      sx={{
        px: 1, py: isTimeline ? 0.3 : (dense ? 0.6 : 0.85), borderRadius: '0.5rem', cursor: 'grab',
        border: '1px solid rgba(45,51,53,0.08)', backgroundColor: '#fff',
        display: 'flex', alignItems: (blocks || isTimeline) ? 'flex-start' : 'center', gap: 0.75,
        minWidth: 0, maxWidth: '100%', overflow: 'hidden',
        ...(blockHeight ? { borderLeft: `3px solid ${prioColor(task)}` } : {}),
        ...(isTimeline ? { height: heightPx, overflow: 'hidden' } : (blockHeight ? { minHeight: blockHeight } : {})),
        opacity: isDragging ? 0.4 : 1,
        transform: transform ? `translate(${transform.x}px, ${transform.y}px)` : undefined,
        transition: transition || undefined,
        '&:hover': { borderColor: 'rgba(82,96,117,0.35)' },
        '&:hover .wk-done': { opacity: 1 },
        '&:hover .wk-next': { opacity: 0.7 },
      }}>
      <Box className="wk-done"
        onClick={(e) => { e.stopPropagation(); onComplete?.(task); }}
        onPointerDown={(e) => e.stopPropagation()}
        sx={{
          width: 16, height: 16, borderRadius: '50%', flexShrink: 0, cursor: 'pointer',
          border: `2px solid ${prioColor(task)}`, opacity: 0.35, transition: 'opacity .15s',
          '&:hover': { backgroundColor: prioColor(task) },
        }} />
      {task.flagged && <FlagIcon sx={{ fontSize: 13, color: '#dc2626' }} />}
      {task.checklist?.length > 0 && (
        <span title="Subtaken (uit Microsoft To Do)" style={{ fontSize: 11, fontWeight: 700, color: '#64748b', whiteSpace: 'nowrap' }}>
          ☑ {task.checklist.filter((c) => c.done).length}/{task.checklist.length}
        </span>
      )}
      {task.source === 'local' && (
        <Tooltip title="Oude privé-taak (onze database) — alleen jij ziet 'm"><span style={{ fontSize: 12 }}>🔒</span></Tooltip>
      )}
      {task.source === 'mstodo' && !task.flagged && (
        <Tooltip title="Privé — staat in jouw Microsoft To Do, alleen jij ziet 'm"><span style={{ fontSize: 12 }}>🔒</span></Tooltip>
      )}
      <Typography noWrap sx={{ fontSize: dense ? '0.8rem' : '0.875rem', fontWeight: 600, color: '#374151', flex: 1, minWidth: 0 }}>
        {task.title}
      </Typography>
      {task.hours ? (
        <Typography noWrap sx={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 700, flexShrink: 0 }}>{task.hours}u</Typography>
      ) : null}
      {task.projectName && (
        <Typography noWrap sx={{ fontSize: '0.7rem', color: '#94a3b8', maxWidth: 100 }}>{task.projectName}</Typography>
      )}
      {/* Geen 'To-Do'-label op elk item (overkill) — alleen de uitzonderingen taggen */}
      {task.source === 'airtable' && !task.startDate && <Chip label="Backlog" size="small" sx={{ height: 17, fontSize: '0.62rem' }} />}
      {task.source === 'meeting' && <Chip label="🎙" size="small" sx={{ height: 17, fontSize: '0.62rem' }} />}
      {onNextWeek && task.source === 'airtable' && task.startDate && (
        <Tooltip title="Naar volgende week (zelfde weekdag)">
          <Box className="wk-next"
            onClick={(e) => { e.stopPropagation(); onNextWeek(task); }}
            onPointerDown={(e) => e.stopPropagation()}
            sx={{ opacity: 0, cursor: 'pointer', color: '#94a3b8', fontSize: '0.85rem', px: 0.25, transition: 'opacity .15s', '&:hover': { color: '#526075' } }}>
            »
          </Box>
        </Tooltip>
      )}
    </Paper>
  );
}

// Draggable variant (todolijst, dagkolommen, tijdlijn-agenda): tussen dagen / naar de lijst.
function TaskChip(props) {
  const dnd = useDraggable({ id: props.task.dragId, data: { task: props.task } });
  return <TaskChipInner {...props} {...dnd} />;
}

// Sortable variant (taken-lijst ónder de tijdlijn): herordenen binnen dezelfde dag.
function SortableTaskChip({ day, ...props }) {
  const dnd = useSortable({ id: props.task.dragId, data: { task: props.task, day } });
  return <TaskChipInner {...props} {...dnd} />;
}

// ── Dag-kolom (droppable) ────────────────────────────────────────────────────
/** "3u45" / "45m" — voor de dagtotalen in de kop. */
function fmtUren(min) {
  const m = Math.max(0, Math.round(min || 0));
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60), r = m % 60;
  return r ? `${h}u${String(r).padStart(2, '0')}` : `${h}u`;
}

/** Totalen per dag (Joris, 6 sep): hoeveel agenda en hoeveel taakwerk staat er? */
function dagTotalen(events, tasks) {
  const agendaMin = (events || []).reduce((acc, ev) => {
    const d = (new Date(ev.end) - new Date(ev.start)) / 60000;
    return acc + (Number.isFinite(d) && d > 0 && d < 24 * 60 ? d : 0);
  }, 0);
  const taken = (tasks || []).filter((t) => t.source !== 'event');
  const taakUren = taken.reduce((acc, t) => acc + (Number(t.hours) || 0), 0);
  return { agendaMin, aantalTaken: taken.length, taakUren, aantalAgenda: (events || []).length };
}

function DayColumn({ dateStr, isToday, isPast, events, tasks, onClickTask, onCompleteTask, onHideEvent, onHideDay, onNextWeek, loadError, onRetry, blocks }) {
  const { setNodeRef, isOver } = useDroppable({ id: `day-${dateStr}` });
  const tot = dagTotalen(events, tasks);
  return (
    <Box ref={setNodeRef} sx={{
      flex: 1, minWidth: 0, borderRadius: '0.75rem', p: 1,
      border: '1px solid', borderColor: isOver ? '#526075' : 'rgba(45,51,53,0.08)',
      backgroundColor: isOver ? '#eef2f6' : isToday ? '#fbfcfe' : '#fff',
      opacity: isPast ? 0.75 : 1,
      display: 'flex', flexDirection: 'column', gap: 0.5,
    }}>
      <Stack direction="row" alignItems="center" sx={{ '&:hover .wk-hideday': { opacity: 0.6 } }}>
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: isToday ? '#526075' : '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', flex: 1 }}>
          {dayNameOf(dateStr)} <span style={{ fontWeight: 500 }}>{dateStr.slice(8)}/{dateStr.slice(5, 7)}</span>
          {isToday ? ' · vandaag' : ''}
        </Typography>
        {(tot.aantalAgenda > 0 || tot.aantalTaken > 0) && (
          <Tooltip title={`${tot.aantalAgenda} afspraken (${fmtUren(tot.agendaMin)}) · ${tot.aantalTaken} taken${tot.taakUren ? ` (${fmtUren(tot.taakUren * 60)})` : ''}`}>
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#94a3b8', mr: 0.5, whiteSpace: 'nowrap' }}>
              {tot.aantalAgenda > 0 ? `📅 ${fmtUren(tot.agendaMin)}` : ''}{tot.aantalAgenda > 0 && tot.aantalTaken > 0 ? ' · ' : ''}{tot.aantalTaken > 0 ? `✓ ${tot.aantalTaken}${tot.taakUren ? ` · ${fmtUren(tot.taakUren * 60)}` : ''}` : ''}
            </Typography>
          </Tooltip>
        )}
        <Tooltip title="Dag verbergen (bv. vrije dag) — meer ruimte voor de rest">
          <Box className="wk-hideday" onClick={() => onHideDay?.(dateStr)} sx={{
            opacity: 0, cursor: 'pointer', fontSize: '0.75rem', color: '#94a3b8', px: 0.5,
            transition: 'opacity .15s', '&:hover': { color: '#ef4444' },
          }}>✕</Box>
        </Tooltip>
      </Stack>
      {/* Agenda-laadfout (bv. Graph-throttling) niet stil laten — dag lijkt anders leeg */}
      {loadError && (
        <Typography variant="caption" onClick={onRetry} sx={{ color: '#dc2626', cursor: 'pointer' }}>
          ⚠️ Agenda laden mislukt — <u>opnieuw proberen</u>
        </Typography>
      )}
      {/* Agenda dense — × bij hover om een item te verbergen (bv. op je vrije dag) */}
      {events.map((ev) => (
        <Box key={ev.id} sx={{ display: 'flex', gap: 0.5, alignItems: 'baseline', px: 0.5, '&:hover .wk-hide': { opacity: 0.6 } }}>
          <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#4338ca', flexShrink: 0 }}>
            {ev.start.slice(11, 16)}
          </Typography>
          <Typography noWrap
            onClick={() => ev.webLink && window.open(ev.webLink, '_blank', 'noopener')}
            title={ev.subject}
            sx={{
              fontSize: '0.78rem', color: '#64748b', flex: 1,
              cursor: ev.webLink ? 'pointer' : 'default',
              '&:hover': ev.webLink ? { color: '#4338ca', textDecoration: 'underline' } : {},
            }}>
            {ev.subject}
          </Typography>
          {fmtEventDuration(ev) && (
            <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', fontWeight: 700, flexShrink: 0 }}>
              {fmtEventDuration(ev)}
            </Typography>
          )}
          <Box className="wk-hide" onClick={() => onHideEvent?.(ev.id)} sx={{
            opacity: 0, cursor: 'pointer', fontSize: '0.7rem', color: '#94a3b8', px: 0.25,
            transition: 'opacity .15s', '&:hover': { color: '#ef4444' },
          }}>✕</Box>
        </Box>
      ))}
      {events.length > 0 && tasks.length > 0 && <Box sx={{ borderTop: '1px dashed rgba(45,51,53,0.1)', my: 0.25 }} />}
      {/* Taken van deze dag */}
      {tasks.map((t) => <TaskChip key={t.dragId} task={t} onClick={onClickTask} onComplete={onCompleteTask} onNextWeek={onNextWeek} dense blocks={blocks} />)}
    </Box>
  );
}

// ── Tijdlijn-weergave (Outlook-achtig) ───────────────────────────────────────
// Agenda-items staan op hun échte tijd; taken (die alleen een duur hebben, geen
// begintijd) worden ingepland in de vrije gaten van de werkdag. Dichter dan de
// lijst: één blik = 'hoe ziet mijn dag eruit'.
const TL_START = 8, TL_END = 19, TL_PXH = 38; // px/uur — dag-as, iets ruimer

const toDecimalHour = (isoStr) => {
  const d = new Date(isoStr);
  return d.getHours() + d.getMinutes() / 60;
};

// Outlook-stijl kolom-layout: overlappende afspraken naast elkaar i.p.v. over elkaar
// (anders valt een dubbel-geboekte meeting weg). Clustert transitief-overlappende
// events en geeft elk een kolom-index (_col) + aantal kolommen in het cluster (_cols).
function layoutTimedColumns(events) {
  const sorted = [...events].sort((a, b) => a.s - b.s || a.e - b.e);
  const out = [];
  let cluster = [], clusterEnd = -Infinity;
  const flush = () => {
    const colEnds = []; // eindtijd van de laatste event per kolom
    cluster.forEach((ev) => {
      let placed = false;
      for (let i = 0; i < colEnds.length; i++) {
        if (ev.s >= colEnds[i] - 1e-9) { ev._col = i; colEnds[i] = ev.e; placed = true; break; }
      }
      if (!placed) { ev._col = colEnds.length; colEnds.push(ev.e); }
    });
    cluster.forEach((ev) => { ev._cols = colEnds.length; out.push(ev); });
    cluster = [];
  };
  sorted.forEach((ev) => {
    if (cluster.length && ev.s >= clusterEnd - 1e-9) { flush(); clusterEnd = -Infinity; }
    cluster.push(ev);
    clusterEnd = Math.max(clusterEnd, ev.e);
  });
  flush();
  return out;
}

function TimelineDayColumn({ dateStr, isToday, isPast, events, tasks, onClickTask, onCompleteTask, onHideEvent, onHideDay, onNextWeek, loadError, onRetry, axisStart = TL_START, axisEnd = TL_END }) {
  const { setNodeRef, isOver } = useDroppable({ id: `day-${dateStr}` });

  const allDay = events.filter((ev) => ev.isAllDay);
  const timed = events.filter((ev) => !ev.isAllDay && ev.start)
    .map((ev) => {
      const s = toDecimalHour(ev.start);
      const e = ev.end ? toDecimalHour(ev.end) : s + 0.5;
      return { ev, s, e: Math.max(s + 0.25, e) };
    })
    .sort((a, b) => a.s - b.s);

  // Taken (todo's) staan als lijst ónder de tijdlijn. De as (start/eind) is gedeeld
  // over de hele week (zie parent) zodat de dagen uitlijnen én we geen ruimte verspillen.
  const gridStart = axisStart, gridEnd = axisEnd;
  const contentH = (gridEnd - gridStart) * TL_PXH;
  const yOf = (h) => (Math.max(gridStart, Math.min(gridEnd, h)) - gridStart) * TL_PXH;
  const nowH = isToday ? (new Date().getHours() + new Date().getMinutes() / 60) : null;

  const hourLines = [];
  for (let h = gridStart; h <= gridEnd; h++) hourLines.push(h);

  return (
    <Box ref={setNodeRef} sx={{
      flex: 1, minWidth: 0, borderRadius: '0.75rem', p: 1,
      border: '1px solid', borderColor: isOver ? '#526075' : 'rgba(45,51,53,0.08)',
      backgroundColor: isOver ? '#eef2f6' : isToday ? '#fbfcfe' : '#fff',
      opacity: isPast ? 0.75 : 1, display: 'flex', flexDirection: 'column', gap: 0.5,
    }}>
      <Stack direction="row" alignItems="center" sx={{ '&:hover .wk-hideday': { opacity: 0.6 } }}>
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: isToday ? '#526075' : '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', flex: 1 }}>
          {dayNameOf(dateStr)} <span style={{ fontWeight: 500 }}>{dateStr.slice(8)}/{dateStr.slice(5, 7)}</span>
          {isToday ? ' · vandaag' : ''}
        </Typography>
        <Tooltip title="Dag verbergen (bv. vrije dag) — meer ruimte voor de rest">
          <Box className="wk-hideday" onClick={() => onHideDay?.(dateStr)} sx={{ opacity: 0, cursor: 'pointer', fontSize: '0.75rem', color: '#94a3b8', px: 0.5, transition: 'opacity .15s', '&:hover': { color: '#ef4444' } }}>✕</Box>
        </Tooltip>
      </Stack>
      {loadError && (
        <Typography variant="caption" onClick={onRetry} sx={{ color: '#dc2626', cursor: 'pointer' }}>
          ⚠️ Agenda laden mislukt — <u>opnieuw proberen</u>
        </Typography>
      )}
      {allDay.map((ev) => (
        <Typography key={ev.id} noWrap title={ev.subject}
          onClick={() => ev.webLink && window.open(ev.webLink, '_blank', 'noopener')}
          sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#4338ca', bgcolor: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '0.4rem', px: 0.5, py: 0.15, cursor: ev.webLink ? 'pointer' : 'default' }}>
          {ev.subject}
        </Typography>
      ))}

      <Box sx={{ position: 'relative', height: contentH, mt: 0.25 }}>
        {hourLines.map((h) => (
          <Box key={h} sx={{ position: 'absolute', top: (h - gridStart) * TL_PXH, left: 0, right: 0, borderTop: '1px dashed rgba(45,51,53,0.07)' }}>
            <Typography sx={{ position: 'absolute', top: -6, left: 0, fontSize: '0.58rem', color: '#cbd5e1', fontWeight: 700, bgcolor: '#fff', pr: 0.3, lineHeight: 1 }}>
              {String(h).padStart(2, '0')}
            </Typography>
          </Box>
        ))}
        {nowH != null && nowH >= gridStart && nowH <= gridEnd && (
          <Box sx={{ position: 'absolute', top: yOf(nowH), left: 22, right: 0, borderTop: '2px solid #ef4444', zIndex: 4 }} />
        )}
        {layoutTimedColumns(timed).map((b) => {
          const top = yOf(b.s);
          const h = Math.max(15, yOf(b.e) - top - 2);
          const cols = b._cols || 1;
          const col = b._col || 0;
          // Overlappende afspraken naast elkaar (Outlook-stijl): kolom rechts van de 24px-gutter.
          const left = `calc(24px + (100% - 24px) * ${col / cols})`;
          const width = `calc((100% - 24px) / ${cols} - 2px)`;
          return (
            <Box key={b.ev.id} title={b.ev.subject}
              onClick={() => b.ev.webLink && window.open(b.ev.webLink, '_blank', 'noopener')}
              sx={{ position: 'absolute', left, width, top, height: h, bgcolor: '#eef2ff', border: '1px solid #c7d2fe', borderLeft: '3px solid #4338ca', borderRadius: '0.4rem', px: 0.5, overflow: 'hidden', cursor: b.ev.webLink ? 'pointer' : 'default', zIndex: 1, '&:hover .wk-hide': { opacity: 0.6 } }}>
              <Typography noWrap sx={{ fontSize: '0.66rem', fontWeight: 700, color: '#4338ca' }}>
                {b.ev.start.slice(11, 16)} {b.ev.subject}
              </Typography>
              <Box className="wk-hide" onClick={(e) => { e.stopPropagation(); onHideEvent?.(b.ev.id); }}
                sx={{ position: 'absolute', top: 1, right: 2, opacity: 0, cursor: 'pointer', fontSize: '0.65rem', color: '#94a3b8', transition: 'opacity .15s', '&:hover': { color: '#ef4444' } }}>✕</Box>
            </Box>
          );
        })}
        {timed.length === 0 && (
          <Typography sx={{ position: 'absolute', top: 6, left: 26, fontSize: '0.72rem', color: '#cbd5e1' }}>geen agenda</Typography>
        )}
      </Box>

      {/* Taken (todo's) van deze dag — als lijst ónder de tijdlijn, niet tussen de agenda. */}
      {tasks.length > 0 && (
        <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px dashed rgba(45,51,53,0.12)', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#94a3b8' }}>
            Taken
          </Typography>
          <SortableContext items={tasks.map((t) => t.dragId)} strategy={verticalListSortingStrategy}>
            {tasks.map((t) => (
              <SortableTaskChip key={t.dragId} task={t} day={dateStr} onClick={onClickTask} onComplete={onCompleteTask} onNextWeek={onNextWeek} dense />
            ))}
          </SortableContext>
        </Box>
      )}
    </Box>
  );
}

/** Airtable-record → taak zoals het weekplan hem toont; `_raw` voor het TaakPaneel. */
function naarWeekTaak(rec) {
  const f = rec.fields || {};
  return {
    dragId: `at-${rec.id}`, _airtableId: rec.id, source: 'airtable', _raw: rec,
    title: f.NaamTaak || 'Naamloos', priority: f.PrioNr || 'Geen prio',
    type: f.Type || null,
    startDate: f.StartDateUitvoerder || null, deadline: f.Deadline || null,
    notes: f.Opmerking || '', hours: parseFloat(f.UrenWerk) || null,
    projectName: (Array.isArray(f['Prnr en Naam']) ? f['Prnr en Naam'][0] : f['Prnr en Naam']) || null,
    projectId: f.Project?.[0] || null,
    isComplete: f.PrioNr === 'Afgerond' || !!f.Afgerond,
  };
}

export default function WeekPlan() {
  const { instance: msalInstance, accounts } = useMsal();
  const navigate = useNavigate();
  const userEmail = (accounts[0]?.username || '').toLowerCase();

  const [dayShift, setDayShift] = useState(0);   // venster schuift per werkdag
  const [events, setEvents] = useState({});       // dateStr -> agenda-items
  const [tasks, setTasks] = useState([]);          // Airtable-taken (mijn taken)
  const [localTasks, setLocalTasks] = useState([]); // 🔒 privé/lokale taken (niet in Airtable)
  const [todos, setTodos] = useState([]);          // MS To-Do + gevlagde mails
  const [loading, setLoading] = useState(true);
  const [listFilter, setListFilter] = useState('alles');
  const [quickAdd, setQuickAdd] = useState('');
  const [listSearch, setListSearch] = useState('');
  const [nieuweStap, setNieuweStap] = useState(''); // subtaak toevoegen in het taakvenster // zoeken in de takenlijst (Joris, 6 sep)
  // Handmatige volgorde van taken per dag (sleep-sorteren in de tijdlijn) — lokaal onthouden.
  const [dayOrder, setDayOrder] = useState(() => {
    try { return JSON.parse(localStorage.getItem('weekplan-day-order') || '{}'); } catch { return {}; }
  });
  const saveDayOrder = (next) => {
    setDayOrder(next);
    try { localStorage.setItem('weekplan-day-order', JSON.stringify(next)); } catch { /* quota */ }
  };
  const [listCollapsed, setListCollapsed] = useState(() => localStorage.getItem('weekplan-todos-collapsed') === '1');
  const toggleListCollapsed = () => setListCollapsed((v) => {
    const next = !v;
    try { localStorage.setItem('weekplan-todos-collapsed', next ? '1' : '0'); } catch { /* quota */ }
    return next;
  });
  const [detail, setDetail] = useState(null);      // taak-detail dialog
  const [projects, setProjects] = useState(null);
  // Weergave: 'tijden' (compacte lijst) of 'blokken' (hoogte = duur).
  const [viewMode, setViewMode] = useState(() => (localStorage.getItem('weekplan-view') === 'blokken' ? 'blokken' : 'tijden'));
  const setView = (v) => { if (v) { setViewMode(v); try { localStorage.setItem('weekplan-view', v); } catch { /* quota */ } } };
  // Weggeklikte agenda-items (bv. dagstart op je vrije dag) — lokaal onthouden
  const [hiddenEvents, setHiddenEvents] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('weekplan-hidden-events') || '[]')); }
    catch { return new Set(); }
  });
  const hideEvent = (id) => {
    setHiddenEvents((prev) => {
      const next = new Set(prev); next.add(id);
      localStorage.setItem('weekplan-hidden-events', JSON.stringify([...next].slice(-300)));
      return next;
    });
  };
  // Hele dag verbergen (vrije dag) — de overige kolommen krijgen de ruimte.
  const [hiddenDays, setHiddenDays] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('weekplan-hidden-days') || '[]')); }
    catch { return new Set(); }
  });
  const toggleDay = (dateStr) => {
    setHiddenDays((prev) => {
      const next = new Set(prev);
      if (next.has(dateStr)) next.delete(dateStr); else next.add(dateStr);
      localStorage.setItem('weekplan-hidden-days', JSON.stringify([...next].slice(-30)));
      return next;
    });
  };

  // Rollend venster: [gisteren, vandaag, +1, +2, +3] in wérkdagen — met de
  // pijltjes schuift alles één werkdag op.
  const days = useMemo(() => {
    let anchor = new Date();
    anchor.setHours(12, 0, 0, 0);
    while (anchor.getDay() === 0 || anchor.getDay() === 6) anchor.setDate(anchor.getDate() + 1);
    if (dayShift !== 0) anchor = addWorkdays(anchor, dayShift);
    return [-1, 0, 1, 2, 3].map((n) => iso(n === 0 ? anchor : addWorkdays(anchor, n)));
  }, [dayShift]);
  const todayIso = iso(new Date());

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  // Sleep-doel bepalen op de CURSORPOSITIE (niet op het midden van het gesleepte
  // kaartje). Voorkomt dat een lange taak op de vorige kolom valt (woensdag→dinsdag).
  const collision = useCallback((args) => {
    const hits = pointerWithin(args);
    const list = hits.length ? hits : rectIntersection(args);
    // Voorkeur voor een taak-droppable (sortable) boven de dag-container, zodat
    // sleep-sorteren binnen een dag op de juiste positie invoegt.
    const taskHit = list.find((h) => { const id = String(h.id); return !id.startsWith('day-') && id !== 'todo-list'; });
    return taskHit ? [taskHit, ...list.filter((h) => h !== taskHit)] : list;
  }, []);

  // ── Data laden ──
  // Kalenderblokken (staan al in de agenda) horen NIET in de takenlijst:
  // afspraken, draaidagen, vrije dagen, vanallesuurtjes (zie CLAUDE.md-learning).
  const CALENDAR_BLOCK_TYPES = useMemo(() => new Set([
    'Afspraak', 'Vrij/intern', 'Draaidag', 'Vanallesuurtje', 'Vrije dag',
  ]), []);
  const loadTasks = useCallback(async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/my-tasks?email=${encodeURIComponent(userEmail)}`);
      const d = await leesJson(r, 'Taken laden');
      setTasks((d.tasks || []).map(naarWeekTaak).filter((t) => !t.isComplete
        && !CALENDAR_BLOCK_TYPES.has(t.priority)
        && !CALENDAR_BLOCK_TYPES.has(t.type)
        // Vangnet op naam: vanallesuurtjes e.d. zijn kalendervulling, geen taak
        && !/vanallesuurtje|focustijd|dagstart|^lunch$/i.test(t.title.trim())));
    } catch (e) { meldFout(e, 'Taken laden'); /* volgende refresh */ }
  }, [userEmail, CALENDAR_BLOCK_TYPES]);

  // 🔒 Lokale (privé) taken — staan alleen in de lokale database, niet in Airtable.
  const loadLocal = useCallback(async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/weekplan-tasks`);
      const d = await leesJson(r, 'Privé-taken laden');
      setLocalTasks((d.tasks || []).map((t) => ({
        dragId: `lk-${t.id}`, _localId: t.id, source: 'local', local: true,
        title: t.title || 'Naamloos', priority: t.priority || 'Geen prio',
        notes: t.notes || '', startDate: t.start_date || null, deadline: t.deadline || null,
        hours: t.hours || null, projectName: null, flagged: false,
      // Ongeplande privé-taken: nieuwste bovenaan (hoogste id) — anders verdween een net
      // omgezette to-do onderaan tussen honderden andere (Joris, 6 sep).
      })).sort((a, b) => (a.startDate ? 1 : 0) - (b.startDate ? 1 : 0) || (a.startDate && b.startDate ? String(a.startDate).localeCompare(String(b.startDate)) : (b._localId || 0) - (a._localId || 0))));
    } catch (e) { meldFout(e, 'Privé-taken laden'); /* volgende refresh */ }
  }, []);

  // Actiepunten uit recente meeting-notities (FF Recorder). Afvinken = lokaal
  // verbergen (er is geen bron-status om af te vinken); inplannen = Airtable-taak.
  const dismissedMeetingItems = useMemo(() => {
    try { return new Set(JSON.parse(localStorage.getItem('weekplan-dismissed-meeting-items') || '[]')); }
    catch { return new Set(); }
  }, []);
  // Actiepunten worden (nog) niet als apart veld opgeslagen — wel staat er een
  // "## Actiepunten"-blok in de samenvatting. Parse dat en filter op MIJN naam
  // (bullets onder "- **Joris**:" of zonder eigenaar).
  const parseActionItems = useCallback((summary, firstName) => {
    const m = String(summary || '').match(/##\s*Actiepunten\s*\n([\s\S]*?)(?=\n##\s|$)/i);
    if (!m) return [];
    const items = [];
    let owner = null;
    for (const raw of m[1].split('\n')) {
      const top = raw.match(/^-\s+\*\*(.+?)\*\*:?\s*(.*)$/);
      const sub = raw.match(/^\s+-\s+(.*)$/);
      const plain = raw.match(/^-\s+(.*)$/);
      if (top) { owner = top[1]; if (top[2].trim()) items.push({ owner, text: top[2] }); }
      else if (sub && owner) items.push({ owner, text: sub[1] });
      else if (plain) { owner = null; items.push({ owner: null, text: plain[1] }); }
    }
    return items
      .filter((i) => !i.owner || i.owner.toLowerCase().includes(firstName))
      .map((i) => i.text.replace(/\*\*/g, '').trim())
      .filter((t) => t.length > 3);
  }, []);

  const loadMeetingActions = useCallback(async () => {
    if (!userEmail) return [];
    const firstName = userEmail.split('@')[0].split('.')[0];
    try {
      // Timeout: een koude AI-engine (test schaalt naar 0) mag dit niet minuten
      // laten hangen — dan maar geen meeting-actiepunten deze refresh.
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 12000);
      const r = await authFetch(`${AI_ENGINE_URL}/api/v1/meetings/?user_email=${encodeURIComponent(userEmail)}&limit=10`,
        { signal: ctrl.signal }).finally(() => clearTimeout(timer));
      if (!r.ok) return [];
      const meetings = await r.json();
      const items = [];
      for (const m of (Array.isArray(meetings) ? meetings : [])) {
        const when = (m.meeting_date || m.created_at || '').slice(0, 10);
        const actions = (Array.isArray(m.action_items) && m.action_items.length)
          ? m.action_items
          : parseActionItems(m.summary, firstName);
        for (const [idx, text] of actions.entries()) {
          const dragId = `mt-${m.id}-${idx}`;
          if (dismissedMeetingItems.has(dragId) || !String(text || '').trim()) continue;
          items.push({
            dragId, source: 'meeting', listId: null, msId: null, meetingId: m.id,
            title: String(text).trim(), importance: 'normal', flagged: false,
            notes: `Actiepunt uit meeting "${m.title || 'meeting'}"${when ? ` (${when})` : ''}`,
            startDate: null, projectName: null,
          });
        }
      }
      return items;
    } catch { return []; }
  }, [userEmail, dismissedMeetingItems, parseActionItems]);

  const [todoError, setTodoError] = useState(null);
  const loadTodos = useCallback(async () => {
    try {
      setTodoError(null);
      const lists = await getTodoLists();
      // Let op: Graph spelt het veld als 'wellknownListName' (kleine k)
      const known = (l) => l.wellknownListName || l.wellKnownListName || '';
      const wanted = (lists || []).filter((l) => known(l) === 'defaultList' || known(l) === 'flaggedEmails');
      const all = [];
      for (const l of wanted) {
        try {
          const items = await getTasksFromList(l.id, false);
          for (const t of (items || [])) {
            all.push({
              dragId: `ms-${t.id}`, source: 'mstodo', listId: l.id, msId: t.id,
              title: t.title || 'Taak', importance: t.importance || 'normal',
              flagged: known(l) === 'flaggedEmails',
              notes: t.body?.content || '', projectName: null,
              // Einddatum in To Do = de dag waarop hij in het Weekplan staat (privé blijft privé).
              startDate: todoDueDate(t),
              checklist: normaliseerChecklist(t),
            });
          }
        } catch (le) { console.warn(`[WeekPlan] lijst '${l.displayName}' laden mislukt:`, le.message); }
      }
      // Gearchiveerde geflagde mails eruit: een geflagde mail die je archiveert is
      // afgehandeld — die hoeft niet in het weekplan. (fail-soft: geen filter bij fout)
      let list = all;
      try {
        const archived = await fetchArchivedSubjects();
        if (archived.size) {
          const norm = (s) => String(s || '').replace(/^(re|fw|fwd):\s*/i, '').trim().toLowerCase();
          list = all.filter((t) => !(t.flagged && archived.has(norm(t.title))));
        }
      } catch { /* geen filter bij fout */ }
      // Eerst de To-Do's/vlaggen tonen; meeting-actiepunten komen er asynchroon
      // bij — de lijst mag nooit wachten op de AI-engine.
      setTodos(list);
      loadMeetingActions().then((mt) => {
        if (!mt.length) return;
        setTodos((prev) => {
          const seen = new Set(prev.map((t) => t.dragId));
          return [...prev, ...mt.filter((t) => !seen.has(t.dragId))];
        });
      });
    } catch (e) {
      console.error('[WeekPlan] To-Do laden mislukt:', e);
      setTodoError(e?.message || String(e));
    }
  }, [loadMeetingActions]);

  // To-Do's pas (her)laden zodra de MSAL-login er echt is — anders faalt de
  // eerste load stil en blijft de lijst leeg tot een refresh.
  // Stormrem: MSAL-events kunnen dit effect vaker triggeren (account-context
  // verandert) — max één automatische reload per 15s, anders throttlet Graph (429).
  const lastTodoLoadRef = useRef(0);
  useEffect(() => {
    if (msalInstance && accounts.length > 0) {
      initMicrosoftTodoService(msalInstance, accounts[0]);
      const now = Date.now();
      if (now - lastTodoLoadRef.current < 15000) return;
      lastTodoLoadRef.current = now;
      loadTodos();
    }
  }, [msalInstance, accounts, loadTodos]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadTasks(), loadLocal()]);
      setLoading(false);
    })();
  }, [loadTasks, loadLocal]);

  const [eventErrors, setEventErrors] = useState(new Set());
  // Per-datum cache + rustig sequentieel laden: 5 parallelle calendarView-calls
  // per pijltjes-klik triggerden Graph-throttling (429) → 'Agenda laden mislukt'.
  // Gecachte dagen worden niet opnieuw opgehaald; mislukte dagen krijgen 1 retry.
  const eventsCacheRef = useRef(new Map());
  const loadingEventsRef = useRef(false);
  const loadEvents = useCallback(async (force = false) => {
    if (loadingEventsRef.current) return;
    loadingEventsRef.current = true;
    try {
      const cache = eventsCacheRef.current;
      // Eerst tonen wat we al hebben (direct beeld bij vooruit/terug bladeren)
      setEvents(Object.fromEntries(days.map((d) => [d, cache.get(d) || []])));
      const failed = [];
      for (const d of days) {
        if (!force && cache.has(d)) continue;
        let ok = false;
        for (let attempt = 0; attempt < 2 && !ok; attempt += 1) {
          try {
            const evs = await fetchDayEvents(d);
            cache.set(d, evs);
            ok = true;
            setEvents((prev) => ({ ...prev, [d]: evs }));
          } catch (e) {
            if (attempt === 0) await new Promise((r) => setTimeout(r, 2000));
            else console.warn('[WeekPlan] agenda laden mislukt voor', d, e?.message);
          }
        }
        if (!ok) failed.push(d);
        // Kleine pauze tussen dagen — voorkomt burst-throttling
        await new Promise((r) => setTimeout(r, 250));
      }
      setEventErrors((prev) => {
        const next = new Set([...prev].filter((d) => days.includes(d) && !eventsCacheRef.current.has(d)));
        failed.forEach((d) => next.add(d));
        return next;
      });
    } finally { loadingEventsRef.current = false; }
  }, [days]);
  useEffect(() => { loadEvents(); }, [loadEvents]);

  const loadProjects = useCallback(async () => {
    if (projects) return;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/projects`);
      const d = await leesJson(r, 'Projecten laden');
      setProjects(d.projects || []);
    } catch (e) { meldFout(e, 'Projecten laden'); setProjects([]); }
  }, [projects]);

  // ── Plannen (drop) ──
  // 🎯 Frits' top 5 voor vandaag — op aanvraag (GPT-call), per dag gecachet.
  const [priosCollapsed, setPriosCollapsed] = useState(() => localStorage.getItem('weekplan-frits-collapsed') === '1');
  const togglePriosCollapsed = () => setPriosCollapsed((v) => {
    const next = !v;
    try { localStorage.setItem('weekplan-frits-collapsed', next ? '1' : '0'); } catch { /* quota */ }
    return next;
  });
  // Overdracht: projecten die een collega tijdelijk aan mij heeft overgedragen.
  const [handoversToMe, setHandoversToMe] = useState([]);
  useEffect(() => {
    if (!userEmail) return;
    authFetch(`${BACKEND_URL}/Api/overdracht/mine?email=${encodeURIComponent(userEmail)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setHandoversToMe((d.toMe || []).filter((h) => h.status === 'active')); })
      .catch(() => { /* paneel is optioneel */ });
  }, [userEmail]);

  const [prioMail, setPrioMail] = useState(null); // { title, loading, body, from, webLink }
  const openPrioMail = async (pr) => {
    if (!pr.mail_item_id) return;
    setPrioMail({ title: pr.mail_subject || pr.title, from: pr.mail_from, webLink: pr.web_link, loading: true });
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/items/${pr.mail_item_id}/body`);
      const d = await leesJson(r, 'Mail ophalen');
      setPrioMail((m) => (m ? { ...m, loading: false, body: d.body || '(mailtekst niet beschikbaar)' } : m));
    } catch {
      setPrioMail((m) => (m ? { ...m, loading: false, body: '(mail ophalen mislukt — open hem in Outlook)' } : m));
    }
  };
  const [prios, setPrios] = useState(() => {
    try {
      const c = JSON.parse(sessionStorage.getItem('weekplan-frits-prios') || 'null');
      return c && c.date === iso(new Date()) ? { items: c.items, loading: false } : { items: null, loading: false };
    } catch { return { items: null, loading: false }; }
  });
  // Weggestreepte ("dit klopt niet") dagprioriteiten — lokaal onthouden én meegestuurd
  // naar Frits zodat hij ze niet opnieuw voorstelt (leert ervan).
  const dismissedPriosRef = useRef((() => {
    try { return JSON.parse(localStorage.getItem('weekplan-dismissed-prios') || '[]'); } catch { return []; }
  })());
  const loadPriorities = async () => {
    setPrios((p) => ({ ...p, loading: true }));
    try {
      const exclude = encodeURIComponent((dismissedPriosRef.current || []).join('||'));
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/day-priorities?exclude=${exclude}`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Frits kon geen top 5 maken');
      const dis = new Set((dismissedPriosRef.current || []).map((s) => s.toLowerCase()));
      const items = (d.priorities || []).filter((it) => !dis.has(String(it.title || '').toLowerCase()));
      setPrios({ items, loading: false });
      // Na analyseren meteen uitklappen zodat je de top-5 direct ziet.
      setPriosCollapsed(false);
      try { localStorage.setItem('weekplan-frits-collapsed', '0'); } catch { /* quota */ }
      try { sessionStorage.setItem('weekplan-frits-prios', JSON.stringify({ date: d.date, items })); } catch { /* quota */ }
    } catch (e) { toast.error(e.message); setPrios((p) => ({ ...p, loading: false })); }
  };
  const [dismissDlg, setDismissDlg] = useState(null); // { pr, reason } — 'klopt niet'-reden
  // "Dit klopt niet" (of gewoon wegstrepen): uit beeld + onthouden; bij mail-items ook
  // een leersignaal (mét reden, indien opgegeven) naar Frits' triage-lessen.
  const dismissPriority = (pr, wrong, reason) => {
    setPrios((p) => ({ ...p, items: (p.items || []).filter((x) => x !== pr) }));
    const next = [...new Set([...(dismissedPriosRef.current || []), pr.title])].slice(-60);
    dismissedPriosRef.current = next;
    try { localStorage.setItem('weekplan-dismissed-prios', JSON.stringify(next)); } catch { /* quota */ }
    if (wrong && pr.mail_item_id) {
      const lesson = String(reason || '').trim()
        ? `Niet als dagprioriteit opnemen: "${pr.title}". Reden van de gebruiker: ${String(reason).trim()}`
        : `Niet als dagprioriteit opnemen — dit klopt niet of is al gedaan: "${pr.title}".`;
      authFetch(`${BACKEND_URL}/Api/mail-assistant/items/${pr.mail_item_id}/lesson`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lesson }),
      }).catch(() => {});
    }
    toast.success(wrong ? 'Genoteerd — Frits leert hiervan' : 'Weggestreept');
  };
  // Naar de todolijst (MS To-Do) — geen Airtable-taak; vanuit de lijst kun je
  // hem alsnog op een dag slepen (dan wordt het wél een echte taak).
  const planPriority = async (pr) => {
    try {
      const list = await getDefaultList();
      const created = await createTask(list.id, {
        title: pr.title,
        body: `🎯 Frits' dagprioriteit (${pr.bron || 'mix'}) — ${pr.reason || ''}`,
        importance: 'high',
      });
      setTodos((prev) => [{
        dragId: `ms-${created.id}`, source: 'mstodo', listId: list.id, msId: created.id,
        title: pr.title, importance: 'high', flagged: false,
        notes: `🎯 Frits' dagprioriteit — ${pr.reason || ''}`, startDate: null, projectName: null,
      }, ...prev]);
      setPrios((p) => ({ ...p, items: (p.items || []).filter((x) => x !== pr) }));
      toast.success('In je todolijst gezet ✔');
    } catch (e) { toast.error(e.message || 'Toevoegen mislukt'); }
  };

  const planOnDay = async (task, dateStr) => {
    // Vorige datum (meestal null: uit de lijst gesleept) voor "Ongedaan maken".
    const vorige = task.startDate || null;
    const gepland = `Gepland op ${dateStr.slice(8)}/${dateStr.slice(5, 7)}`;
    if (task.source === 'local') {
      setLocalTasks((prev) => prev.map((t) => (t.dragId === task.dragId ? { ...t, startDate: dateStr } : t)));
      try {
        await authFetch(`${BACKEND_URL}/Api/weekplan-tasks/${task._localId}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ start_date: dateStr }),
        });
        biedOngedaanAan(gepland, async () => {
          await authFetch(`${BACKEND_URL}/Api/weekplan-tasks/${task._localId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ start_date: vorige }) });
          await loadLocal();
        });
      } catch { toast.error('Opslaan mislukt'); loadLocal(); }
    } else if (task.source === 'airtable') {
      setTasks((prev) => prev.map((t) => (t.dragId === task.dragId ? { ...t, startDate: dateStr } : t)));
      try {
        await authFetch(`${BACKEND_URL}/Api/tasks/${task._airtableId}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ StartDateUitvoerder: dateStr, EndDateUitvoerder: dateStr }),
        });
        biedOngedaanAan(gepland, async () => {
          await authFetch(`${BACKEND_URL}/Api/tasks/${task._airtableId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ StartDateUitvoerder: vorige, EndDateUitvoerder: vorige }) });
          await loadTasks();
        });
      } catch { toast.error('Opslaan mislukt'); loadTasks(); }
    } else if (task.source === 'mstodo') {
      // Privé blijft privé: einddatum in To Do zetten, geen Airtable-taak.
      setTodos((prev) => prev.map((t) => (t.dragId === task.dragId ? { ...t, startDate: dateStr } : t)));
      try {
        await updateTask(task.listId, task.msId, { dueDate: dateStr });
        biedOngedaanAan(`${gepland} 🔒`, async () => {
          await updateTask(task.listId, task.msId, { dueDate: vorige });
          await loadTodos();
        });
      } catch { toast.error('Opslaan in To Do mislukt'); loadTodos(); }
    } else {
      // Meeting-actiepunt → echte Airtable-taak. Volledig optimistic:
      // de chip staat DIRECT op de dag als tijdelijke taak en wordt stil
      // vervangen zodra de POST het echte record teruggeeft — de wachttijd op
      // de server was precies het "tikkeltje te lang" (Joris, 26 aug).
      const tmpDragId = `tmp-${Date.now()}`;
      setTodos((prev) => prev.filter((t) => t.dragId !== task.dragId));
      setTasks((prev) => [...prev, {
        dragId: tmpDragId, _airtableId: null, source: 'airtable',
        title: task.title, priority: 'Geen prio', type: null,
        startDate: dateStr, notes: task.notes || '', projectName: null, projectId: null,
      }]);
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/tasks`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            NaamTaak: task.title,
            StartDateUitvoerder: dateStr, EndDateUitvoerder: dateStr,
            PrioNr: 'Geen prio',
            Opmerking: [
              task.source === 'meeting' ? 'Uit meeting-notities (FF Recorder)'
                : task.flagged ? 'Uit gevlagde e-mail (Outlook 🚩)' : 'Uit Microsoft To-Do',
              task.notes,
            ].filter(Boolean).join('\n\n'),
          }),
        });
        const created = await r.json();
        if (!r.ok) throw new Error(created.error || 'Aanmaken mislukt');
        const rec = created.task || created;
        // Tijdelijke chip omkatten naar het echte record (zelfde plek, geen flits).
        setTasks((prev) => prev.map((t) => (t.dragId === tmpDragId
          ? { ...t, dragId: `at-${rec.id || tmpDragId}`, _airtableId: rec.id }
          : t)));
        if (task.source === 'meeting') {
          // Actiepunt is nu een echte taak — blijvend uit de meetings-lijst houden.
          dismissedMeetingItems.add(task.dragId);
          try { localStorage.setItem('weekplan-dismissed-meeting-items', JSON.stringify([...dismissedMeetingItems])); } catch { /* quota */ }
        }
        toast.success('Taak aangemaakt en gepland');
      } catch (e) {
        setTasks((prev) => prev.filter((t) => t.dragId !== tmpDragId));
        setTodos((prev) => [...prev, task]);
        toast.error(e.message || 'Aanmaken mislukt');
      }
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;
    const task = active.data?.current?.task;
    if (!task) return;
    const overId = String(over.id);
    // Sleep-sorteren: losgelaten boven een andere taak (sortable) → herordenen binnen
    // die dag, of — als het een andere dag is — verplaatsen naar die dag.
    const overTask = over.data?.current?.task;
    if (overTask && overId !== String(active.id)) {
      const day = over.data?.current?.day || overTask.startDate;
      if (day && task.startDate === day) {
        const ids = (tasksByDay[day] || []).map((t) => t.dragId);
        const from = ids.indexOf(String(active.id));
        const to = ids.indexOf(overId);
        if (from !== -1 && to !== -1 && from !== to) saveDayOrder({ ...dayOrder, [day]: arrayMove(ids, from, to) });
        return;
      }
      if (day) { planOnDay(task, day); return; }
    }
    if (overId.startsWith('day-')) { planOnDay(task, overId.slice(4)); return; }
    // Terug naar de lijst = datum eraf (werkt voor Airtable- én lokale taken).
    if (overId === 'todo-list' && task.startDate) {
      const vorige = task.startDate; // voor "Ongedaan maken": terug op dezelfde dag
      if (task.source === 'local') {
        setLocalTasks((prev) => prev.map((t) => (t.dragId === task.dragId ? { ...t, startDate: null } : t)));
        authFetch(`${BACKEND_URL}/Api/weekplan-tasks/${task._localId}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ start_date: null }),
        }).then(() => biedOngedaanAan('Uit de planning gehaald', async () => {
          await authFetch(`${BACKEND_URL}/Api/weekplan-tasks/${task._localId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ start_date: vorige }) });
          await loadLocal();
        }), () => loadLocal());
      } else if (task.source === 'airtable') {
        setTasks((prev) => prev.map((t) => (t.dragId === task.dragId ? { ...t, startDate: null } : t)));
        authFetch(`${BACKEND_URL}/Api/tasks/${task._airtableId}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ StartDateUitvoerder: null, EndDateUitvoerder: null }),
        }).then(() => biedOngedaanAan('Uit de planning gehaald', async () => {
          await authFetch(`${BACKEND_URL}/Api/tasks/${task._airtableId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ StartDateUitvoerder: vorige, EndDateUitvoerder: vorige }) });
          await loadTasks();
        }), () => loadTasks());
      } else if (task.source === 'mstodo') {
        setTodos((prev) => prev.map((t) => (t.dragId === task.dragId ? { ...t, startDate: null } : t)));
        updateTask(task.listId, task.msId, { dueDate: null }).then(() => biedOngedaanAan('Uit de planning gehaald 🔒', async () => {
          await updateTask(task.listId, task.msId, { dueDate: vorige });
          await loadTodos();
        }), () => loadTodos());
      }
    }
  };

  // ── Detail-dialog ──
  // Een Airtable-taak opent het gedeelde TaakPaneel; To Do, privé en meeting-actiepunten
  // houden hun eigen venster (checklist, mailvoorbeeld, omzetten).
  const [paneelTaak, setPaneelTaak] = useState(null);
  const naPaneelWijziging = (airtableId, velden) => {
    const werkBij = (t) => (t && t._airtableId === airtableId
      ? { ...naarWeekTaak({ ...t._raw, id: airtableId, fields: { ...(t._raw?.fields || {}), ...velden } }), dragId: t.dragId }
      : t);
    setTasks((prev) => prev.map(werkBij));
    setPaneelTaak(werkBij);
    // Afgerond of verschoven: de lijst en de dagkolommen opnieuw indelen.
    if (['Afgerond', 'StartDateUitvoerder', 'EndDateUitvoerder'].some((k) => k in velden)
      || ['Afgerond', 'Opgestuurd'].includes(velden.PrioNr)) loadTasks();
  };

  const openDetail = (task) => {
    if (task.source === 'airtable' && task._airtableId) { setPaneelTaak(task); return; }
    // Huidige prio voorselecteren zodat het stoplicht klopt bij openen.
    const prioKey = task.source === 'mstodo'
      ? ({ high: 'rood', low: 'groen' }[task.importance] || 'oranje')
      : PRIO_COLORS.find((p) => p.prioNr === task.priority)?.key;
    setDetail({ ...task, prioKey, saving: false });
    loadProjects();
    if (task.flagged) loadMailPreview(task);
  };

  // 📧 Gevlagde mail: de originele e-mail erbij zoeken (op onderwerp, eigen
  // Graph-login) en als preview in de taak-dialog tonen — zelfde idee als Frits.
  const loadMailPreview = async (t) => {
    setDetail((d) => (d && d.dragId === t.dragId ? { ...d, mail: { loading: true } } : d));
    try {
      const clean = (s) => String(s || '').replace(/^(fw|re|fwd):\s*/i, '').trim();
      const results = await searchMail(clean(t.title), { top: 8 });
      const best = results.find((mm) => clean(mm.subject).toLowerCase() === clean(t.title).toLowerCase()) || results[0];
      if (!best) throw new Error('Mail niet teruggevonden in je mailbox');
      const body = await getMailBody(best.id);
      setDetail((d) => (d && d.dragId === t.dragId
        ? { ...d, mail: { loading: false, body, from: best.from, received: best.received, webLink: best.webLink } } : d));
    } catch (e) {
      setDetail((d) => (d && d.dragId === t.dragId ? { ...d, mail: { loading: false, error: e.message } } : d));
    }
  };
  const saveDetail = async () => {
    const t = detail;
    setDetail((d) => ({ ...d, saving: true }));
    try {
      const prio = PRIO_COLORS.find((p) => p.key === t.prioKey);
      if (t.source === 'local') {
        await authFetch(`${BACKEND_URL}/Api/weekplan-tasks/${t._localId}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: t.title, notes: t.notes || '',
            ...(prio ? { priority: prio.prioNr } : {}),
            ...(t.startDate !== undefined ? { start_date: t.startDate || null } : {}),
          }),
        });
        await loadLocal();
        toast.success('Opgeslagen');
      } else if (t.source === 'airtable') {
        await authFetch(`${BACKEND_URL}/Api/tasks/${t._airtableId}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            NaamTaak: t.title, Opmerking: t.notes || '',
            ...(prio ? { PrioNr: prio.prioNr } : {}),
            ...(t.projectSel ? { Project: [t.projectSel.id] } : {}),
            ...(t.startDate !== undefined ? { StartDateUitvoerder: t.startDate || null, EndDateUitvoerder: t.startDate || null } : {}),
          }),
        });
        await loadTasks();
        toast.success('Opgeslagen');
      } else if (t.source === 'mstodo') {
        const importance = { rood: 'high', oranje: 'normal', groen: 'low' }[t.prioKey] || 'normal';
        await updateTask(t.listId, t.msId, { title: t.title, body: t.notes || '', importance, dueDate: t.startDate || null });
        await loadTodos();
        toast.success('Opgeslagen in To Do 🔒');
      }
      setDetail(null);
    } catch { toast.error('Opslaan mislukt'); setDetail((d) => (d ? { ...d, saving: false } : d)); }
  };

  // 🔒 Omzetten Airtable ⇄ lokaal (privé). Airtable→lokaal VERWIJDERT de Airtable-taak;
  // lokaal→Airtable maakt een Airtable-taak aan en verwijdert de lokale.
  // Een Microsoft To-Do (live uit Outlook) is in dit venster niet te bewerken en kon
  // alleen via slepen een taak worden. Nu ook omzetten naar Airtable of privé (Joris, 6 sep).
  const convertTodo = async (t, toLocal) => {
    setDetail((d) => ({ ...d, saving: true }));
    try {
      if (toLocal) { toast('Dit is al een Microsoft To-Do — dat is je privélijst', { icon: 'ℹ️' }); setDetail((d) => (d ? { ...d, saving: false } : d)); return; }
      const r = await authFetch(`${BACKEND_URL}/Api/tasks`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ NaamTaak: t.title, Opmerking: t.notes || '', PrioNr: 'Geen prio' }),
          });
      if (!r.ok) throw new Error('mislukt');
      try { await completeTask(t.listId, t.msId); }
      catch { toast('Kopie staat, maar de To-Do kon niet worden afgevinkt — staat nu dubbel', { icon: '⚠️' }); }
      toast.success(toLocal ? 'Naar privé (lokaal) gezet 🔒 — staat bovenaan in Backlog' : "In Airtable gezet — team ziet 'm nu (tabblad Backlog)");
      await Promise.all([loadTasks(), loadLocal(), loadTodos()]);
      setListFilter('backlog');
      setDetail(null);
    } catch { toast.error('Omzetten mislukt'); setDetail((d) => (d ? { ...d, saving: false } : d)); }
  };

  const convertTask = async (t, toLocal) => {
    // Airtable → privé VERWIJDERT het teamrecord; dat vraagt om een expliciete bevestiging.
    if (toLocal && t.source === 'airtable' && !(await bevestig({
      titel: 'Naar privé verplaatsen?',
      tekst: 'De Airtable-taak wordt verwijderd; het team ziet hem daarna niet meer. Er komt een kopie in jouw Microsoft To Do.',
      knop: 'Verplaatsen',
      gevaarlijk: true,
    }))) return false;
    setDetail((d) => (d ? { ...d, saving: true } : d));
    const prioNr = PRIO_COLORS.find((p) => p.key === t.prioKey)?.prioNr
      || (t.priority && t.priority !== 'Geen prio' ? t.priority : null);
    try {
      if (toLocal && t.source === 'airtable') {
        // Privé = Microsoft To Do (besluit Joris, 6 sep) — niet meer onze eigen tabel.
        const lists = await getTodoLists();
        const known = (l) => l.wellknownListName || l.wellKnownListName || '';
        const doel = lists.find((l) => known(l) === 'defaultList' || l.displayName === 'Taken' || l.displayName === 'Tasks') || lists[0];
        if (!doel) throw new Error('geen To Do-lijst');
        await createTask(doel.id, { title: t.title, body: t.notes || undefined, dueDate: t.startDate || undefined });
        await authFetch(`${BACKEND_URL}/Api/tasks/${t._airtableId}`, { method: 'DELETE' }).catch(() => toast('Kopie staat, maar het origineel kon niet worden verwijderd — staat nu dubbel', { icon: '⚠️' }));
        toast.success('Naar privé (lokaal) verplaatst 🔒 — staat bovenaan in Backlog');
      } else if (!toLocal && t.source === 'local') {
        const r = await authFetch(`${BACKEND_URL}/Api/tasks`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ NaamTaak: t.title, Opmerking: t.notes || '', PrioNr: prioNr || 'Geen prio',
            ...(t.startDate ? { StartDateUitvoerder: t.startDate, EndDateUitvoerder: t.startDate } : {}) }),
        });
        if (!r.ok) throw new Error('mislukt');
        await authFetch(`${BACKEND_URL}/Api/weekplan-tasks/${t._localId}`, { method: 'DELETE' }).catch(() => toast('Staat in Airtable, maar de lokale kopie kon niet worden verwijderd — staat nu dubbel', { icon: '⚠️' }));
        toast.success("In Airtable gezet — team ziet 'm nu");
      }
      await Promise.all([loadTasks(), loadLocal()]);
      setListFilter('backlog');
      setDetail(null);
      return true;
    } catch { toast.error('Omzetten mislukt'); setDetail((d) => (d ? { ...d, saving: false } : d)); return false; }
  };

  // » Naar volgende week: zelfde weekdag +7 dagen (optimistic).
  const moveToNextWeek = async (t) => {
    const vorige = t.startDate;
    const d = new Date(`${t.startDate}T12:00:00`);
    d.setDate(d.getDate() + 7);
    const next = iso(d);
    setTasks((prev) => prev.map((x) => (x.dragId === t.dragId ? { ...x, startDate: next } : x)));
    try {
      await authFetch(`${BACKEND_URL}/Api/tasks/${t._airtableId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ StartDateUitvoerder: next, EndDateUitvoerder: next }),
      });
      biedOngedaanAan(`Naar ${next.slice(8)}/${next.slice(5, 7)} verplaatst`, async () => {
        await authFetch(`${BACKEND_URL}/Api/tasks/${t._airtableId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ StartDateUitvoerder: vorige, EndDateUitvoerder: vorige }) });
        await loadTasks();
      });
    } catch { toast.error('Verplaatsen mislukt'); loadTasks(); }
  };

  // Subtiel afvinken (hover-rondje): Airtable → Afgerond; MS To-Do → completed.
  // ⚡ Snelle taak: minimalistisch — alleen een titel, komt in je MS To-Do
  // standaardlijst en verschijnt direct in de todolijst.
  const addQuickTodo = async () => {
    const title = quickAdd.trim();
    if (!title) return;
    setQuickAdd('');
    // Default = PRIVÉ = jouw Microsoft To Do (besluit 6 sep): veel taken zijn gevoelig en horen
    // niet in Airtable. Team laten meekijken doe je bewust via "In Airtable zetten".
    const tempId = `tmp-${Date.now()}`;
    setTodos((prev) => [{
      dragId: tempId, source: 'mstodo', listId: null, msId: null, title, importance: 'normal', flagged: false,
      notes: '', startDate: null, projectName: null, checklist: [],
    }, ...prev]);
    try {
      const lists = await getTodoLists();
      const known = (l) => l.wellknownListName || l.wellKnownListName || '';
      const doel = lists.find((l) => known(l) === 'defaultList' || l.displayName === 'Taken' || l.displayName === 'Tasks') || lists[0];
      if (!doel) throw new Error('Geen To Do-lijst gevonden');
      const created = await createTask(doel.id, { title });
      setTodos((prev) => prev.map((t) => (t.dragId === tempId
        ? { ...t, dragId: `ms-${created.id}`, listId: doel.id, msId: created.id } : t)));
    } catch (e) {
      setTodos((prev) => prev.filter((t) => t.dragId !== tempId));
      setQuickAdd(title);
      toast.error(`Taak in To Do aanmaken mislukt: ${e.message || ''}`);
    }
  };

  useEffect(() => registreerOngedaanSneltoets(), []);
  const completeAny = async (t) => {
    if (t.source === 'local') {
      setLocalTasks((prev) => prev.filter((x) => x.dragId !== t.dragId));
      try {
        await authFetch(`${BACKEND_URL}/Api/weekplan-tasks/${t._localId}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ done: true }),
        });
        biedOngedaanAan(`"${t.title}" afgerond`, async () => {
          await authFetch(`${BACKEND_URL}/Api/weekplan-tasks/${t._localId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ done: false }) });
          await loadLocal();
        });
      } catch { toast.error('Afronden mislukt'); loadLocal(); }
      return;
    }
    if (t.source === 'meeting') {
      // Geen bron om af te vinken — actiepunt lokaal (blijvend) verbergen.
      setTodos((prev) => prev.filter((x) => x.dragId !== t.dragId));
      dismissedMeetingItems.add(t.dragId);
      try { localStorage.setItem('weekplan-dismissed-meeting-items', JSON.stringify([...dismissedMeetingItems])); } catch { /* quota */ }
      biedOngedaanAan('Actiepunt verborgen', async () => {
        dismissedMeetingItems.delete(t.dragId);
        try { localStorage.setItem('weekplan-dismissed-meeting-items', JSON.stringify([...dismissedMeetingItems])); } catch { /* quota */ }
        setTodos((prev) => [t, ...prev]);
      });
      return;
    }
    if (t.source === 'airtable') {
      setTasks((prev) => prev.filter((x) => x.dragId !== t.dragId));
      try {
        await authFetch(`${BACKEND_URL}/Api/tasks/${t._airtableId}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ PrioNr: 'Afgerond' }),
        });
        const vorige = t.priority && t.priority !== 'Afgerond' ? t.priority : 'Geen prio';
        biedOngedaanAan(`"${t.title}" afgerond`, async () => {
          await authFetch(`${BACKEND_URL}/Api/tasks/${t._airtableId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ PrioNr: vorige, Afgerond: false }) });
          await loadTasks();
        });
      } catch { toast.error('Afronden mislukt'); loadTasks(); }
    } else {
      setTodos((prev) => prev.filter((x) => x.dragId !== t.dragId));
      try {
        await completeTask(t.listId, t.msId);
        biedOngedaanAan(`"${t.title}" afgevinkt in To Do`, async () => {
          await reopenTask(t.listId, t.msId);
          await loadTodos();
        });
      } catch { toast.error('Afvinken mislukt'); loadTodos(); }
    }
  };

  // ── Lijsten ── (Airtable + lokale/privé taken samen)
  // Lokale (privé) taken eerst: een net toegevoegde quick-taak wordt vooraan in
  // localTasks gezet en verschijnt zo bovenaan de backlog — anders verdween 'ie
  // onderaan tussen tientallen Airtable-taken ('ik zie 'm niet meteen terug').
  // Privé = Microsoft To Do (besluit Joris, 6 sep): een To Do met einddatum in de zichtbare
  // week staat in de dagkolom, zonder datum in de lijst. Hij wordt nooit stiekem een
  // Airtable-taak — dat doe je alleen bewust via "In Airtable zetten".
  const plannedTodos = useMemo(() => todos.filter((t) => t.source === 'mstodo' && t.startDate && days.includes(t.startDate)), [todos, days]);
  const allTasks = useMemo(() => [...localTasks, ...tasks, ...plannedTodos], [tasks, localTasks, plannedTodos]);
  const tasksByDay = useMemo(() => {
    const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const map = {};
    days.forEach((d) => {
      // Vangnet: taak die (op titel) al als agenda-item staat niet dubbel tonen.
      const evNames = new Set((events[d] || []).map((e) => norm(e.subject)));
      let list = allTasks.filter((t) => t.startDate === d && !evNames.has(norm(t.title)));
      // Handmatige sleep-volgorde toepassen (onbekende taken achteraan, stabiel).
      const order = dayOrder[d];
      if (order?.length) {
        const rank = (id) => { const i = order.indexOf(id); return i === -1 ? 1e6 : i; };
        list = list.map((t, i) => [t, i]).sort((a, b) => (rank(a[0].dragId) - rank(b[0].dragId)) || (a[1] - b[1])).map(([t]) => t);
      }
      map[d] = list;
    });
    return map;
  }, [days, allTasks, events, dayOrder]);

  const unplanned = useMemo(() => {
    const backlog = allTasks.filter((t) => !t.startDate || (t.startDate < todayIso && !days.includes(t.startDate)));
    const list = [
      ...backlog.map((t) => ({ ...t, kind: 'backlog' })),
      ...todos.filter((t) => !plannedTodos.includes(t)).map((t) => ({ ...t, kind: t.source === 'meeting' ? 'meeting' : t.flagged ? 'vlag' : 'todo' })),
    ];
    // Zoeken gaat over álle bronnen heen, ongeacht het gekozen tabblad.
    const q = listSearch.trim().toLowerCase();
    if (q) return list.filter((t) => [t.title, t.notes, t.projectName].some((v) => String(v || '').toLowerCase().includes(q)));
    if (listFilter === 'alles') return list;
    if (listFilter === 'vlag') return list.filter((t) => t.kind === 'vlag');
    if (listFilter === 'todo') return list.filter((t) => t.kind === 'todo');
    if (listFilter === 'meeting') return list.filter((t) => t.kind === 'meeting');
    return list.filter((t) => t.kind === 'backlog');
  }, [allTasks, todos, plannedTodos, listFilter, listSearch, days, todayIso]);

  // Gedeelde tijd-as voor de Tijdlijn: start bij de vroegste afspraak, eind bij de
  // laatste (max 18u) over de zichtbare dagen — dagen lijnen zo uit én we verspillen
  // geen ruimte met een vaste 8–19.
  const weekAxis = useMemo(() => {
    const visible = days.filter((d) => !hiddenDays.has(d));
    let earliest = null, latest = null;
    visible.forEach((d) => {
      (events[d] || []).forEach((ev) => {
        if (ev.isAllDay || !ev.start) return;
        const s = new Date(ev.start).getHours() + new Date(ev.start).getMinutes() / 60;
        const e = ev.end ? new Date(ev.end).getHours() + new Date(ev.end).getMinutes() / 60 : s + 0.5;
        earliest = earliest == null ? s : Math.min(earliest, s);
        latest = latest == null ? e : Math.max(latest, e);
      });
    });
    const start = earliest == null ? 8 : Math.max(6, Math.min(9, Math.floor(earliest)));
    const end = latest == null ? 18 : Math.min(18, Math.max(start + 3, Math.ceil(latest)));
    return { start, end };
  }, [days, hiddenDays, events]);

  const { setNodeRef: listRef, isOver: listOver } = useDroppable({ id: 'todo-list' });

  return (
    <DndContext sensors={sensors} collisionDetection={collision} onDragEnd={handleDragEnd}>
      <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 4 }, pb: 4, display: 'flex', flexDirection: 'column' }}>
        <PageHeader
          icon="calendar_view_week"
          title="Weekplan"
          subtitle="Gisteren · vandaag · de komende 3 werkdagen — schuif met de pijltjes. Sleep taken uit de lijst naar een dag; gevlagde mails (🚩) verschijnen automatisch."
          rightActions={(
            <Stack direction="row" spacing={0.5} alignItems="center">
              <ToggleButtonGroup size="small" exclusive value={viewMode} onChange={(_e, v) => setView(v)} sx={{ mr: 1 }}>
                <ToggleButton value="tijden" sx={{ textTransform: 'none', fontWeight: 700, py: 0.25 }}>Lijst</ToggleButton>
                <ToggleButton value="blokken" sx={{ textTransform: 'none', fontWeight: 700, py: 0.25 }}>Tijdlijn</ToggleButton>
              </ToggleButtonGroup>
              <IconButton size="small" onClick={() => setDayShift((v) => v - 1)}><ChevronLeftIcon /></IconButton>
              <Button size="small" onClick={() => setDayShift(0)} sx={{ textTransform: 'none', fontWeight: 700 }}>
                {dayShift === 0 ? 'Vandaag' : `${dayShift > 0 ? '+' : ''}${dayShift} werkdag${Math.abs(dayShift) === 1 ? '' : 'en'}`}
              </Button>
              <IconButton size="small" onClick={() => setDayShift((v) => v + 1)}><ChevronRightIcon /></IconButton>
            </Stack>
          )}
        />

        {/* Overgedragen aan jou: collega afwezig, jij neemt deze projecten waar */}
        {handoversToMe.length > 0 && (
          <Alert severity="warning" icon={<span>🔁</span>} sx={{ mb: 1.5 }}
            action={<Button size="small" href="/overdracht" sx={{ textTransform: 'none', fontWeight: 700 }}>Bekijk dossiers</Button>}>
            <b>{handoversToMe[0].from_name || handoversToMe[0].from_email}</b> heeft {handoversToMe.length} project{handoversToMe.length === 1 ? '' : 'en'} aan
            jou overgedragen{handoversToMe[0].period_end ? ` (t/m ${new Date(handoversToMe[0].period_end).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })})` : ''}:
            {' '}{handoversToMe.slice(0, 4).map((h) => h.project_name).join(' · ')}{handoversToMe.length > 4 ? ' · …' : ''}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
        ) : (
          <>
            {/* Week-grid — geen interne scroll: kolommen groeien met de inhoud mee */}
            <Stack direction="row" spacing={1} sx={{ alignItems: 'stretch', mb: 1 }}>
              {days.filter((d) => !hiddenDays.has(d)).map((d) => {
                const common = {
                  dateStr: d, isToday: d === todayIso, isPast: d < todayIso,
                  events: (events[d] || []).filter((ev) => !hiddenEvents.has(ev.id)),
                  tasks: tasksByDay[d] || [], onClickTask: openDetail,
                  onCompleteTask: completeAny, onHideEvent: hideEvent, onHideDay: toggleDay,
                  onNextWeek: moveToNextWeek, loadError: eventErrors.has(d), onRetry: loadEvents,
                };
                return viewMode === 'blokken'
                  ? <TimelineDayColumn key={d} {...common} axisStart={weekAxis.start} axisEnd={weekAxis.end} />
                  : <DayColumn key={d} {...common} blocks={false} />;
              })}
            </Stack>
            {/* Verborgen dagen terughalen */}
            {days.some((d) => hiddenDays.has(d)) && (
              <Stack direction="row" spacing={0.75} sx={{ mb: 1.5 }}>
                {days.filter((d) => hiddenDays.has(d)).map((d) => (
                  <Chip key={d} size="small" variant="outlined" onClick={() => toggleDay(d)}
                    label={`${dayNameOf(d)} ${d.slice(8)}/${d.slice(5, 7)} tonen`}
                    sx={{ fontSize: '0.7rem', color: '#94a3b8' }} />
                ))}
              </Stack>
            )}

            {/* 🎯 Frits' top 5 voor vandaag — op aanvraag, direct in te plannen */}
            <Paper variant="outlined" sx={{ borderRadius: '0.75rem', p: 1.5, mb: 1, bgcolor: '#fbfaff', borderColor: '#e9e5f8' }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <IconButton size="small" onClick={togglePriosCollapsed} sx={{ mr: -0.5 }}>
                  <ChevronRightIcon fontSize="small" sx={{ transform: priosCollapsed ? 'none' : 'rotate(90deg)', transition: 'transform .15s' }} />
                </IconButton>
                <Typography onClick={togglePriosCollapsed} sx={{ fontWeight: 800, fontSize: '0.95rem', cursor: 'pointer' }}>🎯 Frits' top 5 vandaag</Typography>
                <Button size="small" variant={prios.items ? 'text' : 'outlined'} onClick={loadPriorities} disabled={prios.loading}
                  startIcon={prios.loading ? <CircularProgress size={13} /> : null}
                  sx={{ textTransform: 'none', fontWeight: 700 }}>
                  {prios.loading ? 'Frits denkt na…' : prios.items ? 'Ververs' : 'Vraag Frits'}
                </Button>
                <Typography variant="caption" color="text.disabled">
                  op basis van je mails, taken & deadlines, meeting-actiepunten en agenda
                </Typography>
              </Stack>
              {!priosCollapsed && prios.items && prios.items.length > 0 && (
                <Stack spacing={0.5} sx={{ mt: 1 }}>
                  {prios.items.map((pr, i) => (
                    <Stack key={`${pr.title}-${i}`} direction="row" alignItems="center" spacing={1}
                      sx={{ px: 1, py: 0.6, borderRadius: '0.5rem', bgcolor: '#fff', border: '1px solid rgba(45,51,53,0.08)' }}>
                      <Typography sx={{ fontWeight: 800, color: '#7c3aed', width: 18, flexShrink: 0 }}>{i + 1}</Typography>
                      <Box onClick={() => openPrioMail(pr)}
                        sx={{ flex: 1, minWidth: 0, cursor: pr.mail_item_id ? 'pointer' : 'default',
                          '&:hover': pr.mail_item_id ? { '& .fp-title': { textDecoration: 'underline' } } : {} }}>
                        <Typography className="fp-title" noWrap sx={{ fontSize: '0.85rem', fontWeight: 700 }}
                          title={pr.mail_item_id ? `${pr.title} — klik om de mail te lezen` : pr.title}>
                          {pr.title}{pr.mail_item_id ? ' ✉' : ''}
                        </Typography>
                        <Typography noWrap variant="caption" color="text.secondary" title={pr.reason}>{pr.reason}</Typography>
                      </Box>
                      <Chip label={pr.bron || 'mix'} size="small" sx={{ height: 18, fontSize: '0.62rem' }} />
                      <Button size="small" variant="outlined" onClick={() => planPriority(pr)}
                        sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap', py: 0 }}>
                        + Naar todolijst
                      </Button>
                      <Tooltip title="Dit klopt niet / is al gedaan — geef de reden, dan leert Frits ervan">
                        <Button size="small" color="inherit" onClick={() => setDismissDlg({ pr, reason: '' })}
                          sx={{ textTransform: 'none', color: '#94a3b8', minWidth: 0, px: 0.75, whiteSpace: 'nowrap' }}>
                          klopt niet
                        </Button>
                      </Tooltip>
                    </Stack>
                  ))}
                </Stack>
              )}
              {!priosCollapsed && prios.items && prios.items.length === 0 && (
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                  Frits ziet vandaag geen urgente prioriteiten — lekker bezig 🎉
                </Typography>
              )}
            </Paper>

            {/* Todolijst (onderste helft) — inklapbaar voor rust; slepen naar een
                ingeklapte lijst werkt gewoon (drop = datum eraf). */}
            <Paper ref={listRef} variant="outlined" sx={{
              height: listCollapsed ? 'auto' : '42vh', borderRadius: '0.75rem', p: 1.5,
              overflowY: listCollapsed ? 'visible' : 'auto',
              borderColor: listOver ? '#526075' : undefined, bgcolor: listOver ? '#f8fafc' : undefined,
            }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: listCollapsed ? 0 : 1 }}>
                <Tooltip title={listCollapsed ? 'Todolijst uitklappen' : 'Todolijst inklappen'}>
                  <IconButton size="small" onClick={toggleListCollapsed} sx={{ mr: -0.5 }}>
                    {listCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" sx={{ transform: 'rotate(90deg)' }} />}
                  </IconButton>
                </Tooltip>
                <Typography onClick={toggleListCollapsed} sx={{ fontWeight: 800, fontSize: '1rem', cursor: 'pointer' }}>
                  Todolijst{listCollapsed ? ` (${unplanned.length})` : ''}
                </Typography>
                {!listCollapsed && (
                  <>
                    <ToggleButtonGroup size="small" exclusive value={listFilter} onChange={(_e, v) => v && setListFilter(v)}
                      sx={{ '& .MuiToggleButton-root': { py: 0.2, px: 1.25, fontSize: 12.5, textTransform: 'none', fontWeight: 700 } }}>
                      <ToggleButton value="alles" disabled={!!listSearch.trim()}>Alles ({unplanned.length})</ToggleButton>
                      <ToggleButton value="backlog">Backlog</ToggleButton>
                      <ToggleButton value="todo">To-Do</ToggleButton>
                      <ToggleButton value="vlag">🚩 Gevlagd</ToggleButton>
                      <ToggleButton value="meeting">🎙 Meetings</ToggleButton>
                    </ToggleButtonGroup>
                    <TextField size="small" placeholder="🔍 Zoek taak… (Esc wist)" value={listSearch}
                      onChange={(e) => setListSearch(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Escape') setListSearch(''); }}
                      sx={{ width: 200, '& .MuiInputBase-input': { py: 0.6, fontSize: 13 } }} />
                    <TextField size="small" placeholder="+ Snelle taak… (Enter)" value={quickAdd}
                      onChange={(e) => setQuickAdd(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addQuickTodo(); } }}
                      sx={{ width: 210, '& .MuiInputBase-input': { py: 0.6, fontSize: 13 } }} />
                    <Tooltip title="🔒 = privé (jouw Microsoft To Do, alleen jij) · zonder slotje = Airtable (team ziet 'm) · 🚩 = gevlagde mail · 🎙 = meeting-actiepunt. Slepen naar een dag houdt privé privé.">
                      <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto', cursor: 'help' }}>
                        🔒 privé · 👥 team — sleep naar een dag om te plannen, terug om de datum eraf te halen
                      </Typography>
                    </Tooltip>
                  </>
                )}
              </Stack>
              {!listCollapsed && (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' }, gap: 0.75 }}>
                {unplanned.map((t) => <TaskChip key={t.dragId} task={t} onClick={openDetail} onComplete={completeAny} />)}
                {unplanned.length === 0 && (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.disabled">
                      Niets in deze lijst — vlag een mail in Outlook (🚩) of voeg een taak toe in To-Do/Airtable.
                    </Typography>
                    {todoError && (
                      <Typography variant="caption" sx={{ color: '#dc2626', display: 'block', mt: 1 }}>
                        ⚠️ To-Do's laden gaf een fout: {todoError} —{' '}
                        <Box component="span" onClick={loadTodos} sx={{ textDecoration: 'underline', cursor: 'pointer' }}>opnieuw proberen</Box>
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
              )}
            </Paper>
          </>
        )}

        {/* 'Klopt niet' — reden opgeven zodat Frits ervan leert */}
        <Dialog open={!!dismissDlg} onClose={() => setDismissDlg(null)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 800 }}>Wat klopt er niet?</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" sx={{ mb: 1.5, color: '#475569' }}>
              “{dismissDlg?.pr?.title}”
            </Typography>
            <TextField autoFocus fullWidth multiline minRows={2} size="small"
              placeholder="Bv. 'al gedaan', 'niet vandaag', 'is geen prioriteit', 'verkeerd begrepen'…"
              value={dismissDlg?.reason || ''}
              onChange={(e) => setDismissDlg((d) => ({ ...d, reason: e.target.value }))} />
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
              Frits leert hiervan en stelt dit soort dingen minder snel voor.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { dismissPriority(dismissDlg.pr, true, ''); setDismissDlg(null); }} sx={{ textTransform: 'none', color: '#94a3b8' }}>
              Zonder reden wegstrepen
            </Button>
            <Button variant="contained" onClick={() => { dismissPriority(dismissDlg.pr, true, dismissDlg.reason); setDismissDlg(null); }}
              sx={{ textTransform: 'none', fontWeight: 700 }}>
              Verstuur — Frits leert hiervan
            </Button>
          </DialogActions>
        </Dialog>

        {/* Mail-preview van een Frits-prioriteit */}
        <Dialog open={!!prioMail} onClose={() => setPrioMail(null)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ fontWeight: 800 }}>✉ {prioMail?.title}</DialogTitle>
          <DialogContent dividers>
            {prioMail?.from && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Van: {prioMail.from}
              </Typography>
            )}
            {prioMail?.loading ? (
              <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={22} /></Stack>
            ) : (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.65, maxHeight: '55vh', overflowY: 'auto' }}>
                {prioMail?.body}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            {prioMail?.webLink && (
              <Button href={prioMail.webLink} target="_blank" rel="noopener" sx={{ textTransform: 'none', mr: 'auto' }}>
                Open in Outlook ↗
              </Button>
            )}
            <Button href="/mail-assistent" sx={{ textTransform: 'none' }}>Open in Frits</Button>
            <Button onClick={() => setPrioMail(null)} sx={{ textTransform: 'none' }}>Sluiten</Button>
          </DialogActions>
        </Dialog>

        <TaakPaneel
          taak={paneelTaak}
          open={Boolean(paneelTaak)}
          onClose={() => setPaneelTaak(null)}
          onBijgewerkt={naPaneelWijziging}
          onVerwijderd={() => { setPaneelTaak(null); loadTasks(); }}
          onNaarProject={(pid) => { setPaneelTaak(null); navigate(`/projects/${pid}`); }}
          extraActies={paneelTaak ? [{
            label: 'Naar privé (To Do)',
            onClick: async () => { if (await convertTask(paneelTaak, true)) setPaneelTaak(null); },
          }] : []}
        />

        {/* Taak-detail voor To Do / privé / meeting: beschrijving + prio-stoplicht */}
        <Dialog open={!!detail} onClose={() => !detail?.saving && setDetail(null)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 800 }}>Taak</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              <TextField fullWidth size="small" label="Taak" value={detail?.title || ''}
                disabled={detail?.source === 'meeting'}
                onChange={(e) => setDetail((d) => ({ ...d, title: e.target.value }))} />
              <TextField fullWidth multiline minRows={6} label="Beschrijving / notities" value={detail?.notes || ''}
                disabled={detail?.source === 'meeting'}
                onChange={(e) => setDetail((d) => ({ ...d, notes: e.target.value }))} />
              {/* Links uit de notities als klikbare chips — lange URL's zijn in de
                  tekst zelf onleesbaar (bv. Outlook-links uit Frits) */}
              {(() => {
                const urls = [...new Set(((detail?.notes || '').match(/https?:\/\/[^\s<>"')]+/g) || []))];
                if (!urls.length) return null;
                return (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {urls.map((u) => {
                      let host = 'link';
                      try { host = new URL(u).hostname.replace(/^www\./, ''); } catch { /* laat 'link' staan */ }
                      const label = /outlook\.(office365|office|live)\.com/.test(host) ? '📧 Open mail in Outlook' : `🔗 ${host}`;
                      return (
                        <Chip key={u} size="small" clickable component="a" href={u} target="_blank" rel="noopener"
                          label={`${label} ↗`} sx={{ fontWeight: 600 }} />
                      );
                    })}
                  </Stack>
                );
              })()}
              {(detail?.source === 'airtable' || detail?.source === 'local') && (
                <>
                  <ToggleButtonGroup exclusive size="small" fullWidth
                    value={detail?.source === 'local' ? 'local' : 'airtable'}
                    onChange={(_e, v) => { if (v && !detail?.saving) convertTask(detail, v === 'local'); }}>
                    <ToggleButton value="local" sx={{ textTransform: 'none', fontWeight: 700 }}>🔒 Alleen voor mij</ToggleButton>
                    <ToggleButton value="airtable" sx={{ textTransform: 'none', fontWeight: 700 }}>👥 In Airtable (team)</ToggleButton>
                  </ToggleButtonGroup>
                  {detail?.source === 'local' && (
                    <Typography variant="caption" sx={{ color: '#c2410c', fontWeight: 700 }}>
                      🔒 Oude privé-taak uit onze eigen database — alleen jij ziet 'm. Nieuwe privé-taken komen in Microsoft To Do;
                      deze zet je over via Privé-to-do's.
                    </Typography>
                  )}
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>Prio:</Typography>
                    {PRIO_COLORS.map((p) => (
                      <Tooltip key={p.key} title={p.label}>
                        <Box onClick={() => setDetail((d) => ({ ...d, prioKey: p.key }))} sx={{
                          width: 26, height: 26, borderRadius: '50%', bgcolor: p.color, cursor: 'pointer',
                          border: detail?.prioKey === p.key ? '3px solid #2d3335' : '3px solid transparent',
                        }} />
                      </Tooltip>
                    ))}
                  </Stack>
                  <TextField type="date" size="small" label="Datum (dag)" InputLabelProps={{ shrink: true }}
                    value={detail?.startDate || ''}
                    onChange={(e) => setDetail((d) => ({ ...d, startDate: e.target.value || null }))} sx={{ maxWidth: 200 }} />
                  {detail?.source === 'airtable' && (
                    <Autocomplete size="small" options={projects || []} loading={projects === null}
                      getOptionLabel={(p) => p.fields?.['Projectnr_en_naam'] || p.fields?.Projectnaam || p.fields?.['Prnr en Naam'] || String(p.id)}
                      value={detail?.projectSel || null}
                      onChange={(_e, v) => setDetail((d) => ({ ...d, projectSel: v }))}
                      renderInput={(params) => <TextField {...params} label={detail?.projectName ? `Project (nu: ${detail.projectName})` : 'Project koppelen'} />} />
                  )}
                </>
              )}
              {detail?.source === 'meeting' && detail?.meetingId && (
                <Button variant="outlined" size="small" href={`/MeetingDashboard/${detail.meetingId}`}
                  sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 700 }}>
                  📝 Open meeting-notities
                </Button>
              )}
              {detail?.source === 'mstodo' && (
                <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5, p: 1.25 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.5 }}>
                    Subtaken{detail.checklist?.length ? ` · ${detail.checklist.filter((c) => c.done).length} van ${detail.checklist.length}` : ''}
                  </Typography>
                  {(detail.checklist || []).map((c) => (
                    <Box key={c.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, '&:hover .sub-del': { opacity: 1 } }}>
                      <Checkbox size="small" checked={c.done} sx={{ p: 0.5 }} onChange={async (e) => {
                        const done = e.target.checked;
                        const zet = (lst) => lst.map((x) => (x.id === c.id ? { ...x, done } : x));
                        setDetail((d) => ({ ...d, checklist: zet(d.checklist) }));
                        setTodos((prev) => prev.map((t) => (t.dragId === detail.dragId ? { ...t, checklist: zet(t.checklist || []) } : t)));
                        try { await setChecklistItem(detail.listId, detail.msId, c.id, done); }
                        catch (err) { toast.error(`Subtaak bijwerken mislukt: ${err.message}`); }
                      }} />
                      <Typography sx={{ fontSize: 14, flex: 1, textDecoration: c.done ? 'line-through' : 'none', color: c.done ? '#94a3b8' : '#0f172a' }}>{c.title}</Typography>
                      <IconButton className="sub-del" size="small" sx={{ opacity: 0, transition: 'opacity .15s' }} onClick={async () => {
                        const weg = (lst) => lst.filter((x) => x.id !== c.id);
                        setDetail((d) => ({ ...d, checklist: weg(d.checklist) }));
                        setTodos((prev) => prev.map((t) => (t.dragId === detail.dragId ? { ...t, checklist: weg(t.checklist || []) } : t)));
                        try { await deleteChecklistItem(detail.listId, detail.msId, c.id); }
                        catch (err) { toast.error(`Subtaak verwijderen mislukt: ${err.message}`); loadTodos(); }
                      }}>
                        <span style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1 }}>✕</span>
                      </IconButton>
                    </Box>
                  ))}
                  <TextField size="small" fullWidth placeholder="+ Volgende stap (Enter)" value={nieuweStap}
                    disabled={!detail.msId} onChange={(e) => setNieuweStap(e.target.value)}
                    onKeyDown={async (e) => {
                      if (e.key !== 'Enter' || !nieuweStap.trim()) return;
                      e.preventDefault();
                      const titel = nieuweStap.trim(); setNieuweStap('');
                      try {
                        const n = await addChecklistItem(detail.listId, detail.msId, titel);
                        const item = { id: n.id, title: n.displayName || titel, done: false };
                        setDetail((d) => ({ ...d, checklist: [...(d.checklist || []), item] }));
                        setTodos((prev) => prev.map((t) => (t.dragId === detail.dragId ? { ...t, checklist: [...(t.checklist || []), item] } : t)));
                      } catch (err) { toast.error(`Subtaak toevoegen mislukt: ${err.message}`); setNieuweStap(titel); }
                    }}
                    sx={{ mt: 0.75, '& .MuiInputBase-input': { py: 0.75, fontSize: 14 } }} />
                </Box>
              )}
              {detail?.source === 'mstodo' && (
                <>
                  <Typography variant="caption" sx={{ color: '#c2410c', fontWeight: 700 }}>
                    🔒 Privé — staat in jouw Microsoft To Do{detail?.flagged ? ' (gevlagde mail)' : ''}. Alleen jij ziet 'm; het team niet.
                    Wijzigingen hier landen direct in To Do.
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>Prio:</Typography>
                    {PRIO_COLORS.map((p) => (
                      <Tooltip key={p.key} title={p.label}>
                        <Box onClick={() => setDetail((d) => ({ ...d, prioKey: p.key }))} sx={{
                          width: 26, height: 26, borderRadius: '50%', bgcolor: p.color, cursor: 'pointer',
                          border: detail?.prioKey === p.key ? '3px solid #2d3335' : '3px solid transparent',
                        }} />
                      </Tooltip>
                    ))}
                  </Stack>
                  <TextField type="date" size="small" label="Datum (dag)" InputLabelProps={{ shrink: true }}
                    value={detail?.startDate || ''}
                    onChange={(e) => setDetail((d) => ({ ...d, startDate: e.target.value || null }))} sx={{ maxWidth: 200 }} />
                  <Button size="small" variant="outlined" disabled={!!detail?.saving} onClick={() => convertTodo(detail, false)}
                    sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 700 }}>
                    👥 In Airtable zetten (team ziet 'm, To Do wordt afgevinkt)
                  </Button>
                </>
              )}
              {/* Preview van de originele mail bij gevlagde items */}
              {detail?.flagged && detail?.mail && (
                <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5, bgcolor: '#f8fafc', p: 1.5 }}>
                  {detail.mail.loading && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={14} />
                      <Typography variant="caption" color="text.secondary">Mail ophalen…</Typography>
                    </Stack>
                  )}
                  {detail.mail.error && (
                    <Typography variant="caption" sx={{ color: '#dc2626' }}>⚠️ {detail.mail.error}</Typography>
                  )}
                  {detail.mail.body && (
                    <>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                        <Typography variant="caption" fontWeight={700}>
                          📧 {detail.mail.from}{detail.mail.received ? ` · ${new Date(detail.mail.received).toLocaleDateString('nl-NL')}` : ''}
                        </Typography>
                        {detail.mail.webLink && (
                          <Typography component="a" href={detail.mail.webLink} target="_blank" rel="noopener"
                            variant="caption" sx={{ ml: 'auto', fontWeight: 700 }}>
                            Open in Outlook ↗
                          </Typography>
                        )}
                      </Stack>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxHeight: 260, overflowY: 'auto', fontSize: 13, lineHeight: 1.65 }}>
                        {detail.mail.body}
                      </Typography>
                    </>
                  )}
                </Box>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            {(detail?.source === 'airtable' || detail?.source === 'local' || detail?.source === 'mstodo') && (
              <Button color="success" startIcon={<CheckCircleOutlineIcon />}
                onClick={() => { const t = detail; setDetail(null); completeAny(t); }}
                sx={{ textTransform: 'none', fontWeight: 700, mr: 'auto' }}>
                Afronden
              </Button>
            )}
            <Button onClick={() => setDetail(null)} disabled={detail?.saving} sx={{ textTransform: 'none' }}>Sluiten</Button>
            {(detail?.source === 'airtable' || detail?.source === 'local' || detail?.source === 'mstodo') && (
              <Button variant="contained" onClick={saveDetail} disabled={detail?.saving}
                startIcon={detail?.saving ? <CircularProgress size={14} color="inherit" /> : null}
                sx={{ textTransform: 'none', fontWeight: 800 }}>
                Opslaan
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </DndContext>
  );
}
